/**
 * §9 Layer 5 rule 5 — the doctor is flagged.
 *
 * Every session that ended early, newest first, with what was on screen, when,
 * and what the monitor actually saw. Nothing here is a summary the software
 * decided on his behalf: the trace is the trace, and it opens.
 *
 * THE RESTORE BUTTON IS THE POINT OF THIS SCREEN, not a footnote
 * (DECISIONS.md 2026-08-20). Decay sensitivity is unvalidated until real
 * patients have generated real sessions, so early false positives are expected.
 * Without a visible one-click undo, a tuning artifact quietly shrinks a
 * patient's program and goes unnoticed for several visits — under-dosing he
 * never chose. The safety behaviour is unchanged by its presence; only the
 * correction is made cheap.
 *
 * The full dashboard exception view is Phase 7. This is the same information
 * where he already is when the question comes up.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api.js';
import { Panel } from '../../components/Panel.js';
import { describeTrigger } from './safetyTrace.js';
import styles from './SafetyEvents.module.css';

interface SafetyEventView {
  id: string;
  type: string;
  triggeredAt: string;
  reviewedAt: string | null;
  exerciseId: string | null;
  sessionLabel: string | null;
  cause: string;
  triggerData: unknown;
  reduction: { id: string; restoredAt: string | null; restoreNote: string | null } | null;
}

interface SafetyEventsResponse {
  progressionLocked: boolean;
  events: SafetyEventView[];
}

export function SafetyEvents({ patientId }: { patientId: string }) {
  const queryClient = useQueryClient();

  const loaded = useQuery({
    queryKey: ['safety-events', patientId],
    queryFn: () => api.get<SafetyEventsResponse>(`/patients/${patientId}/safety-events`),
    retry: false,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['safety-events', patientId] });
    // The gate reads `progressionLocked`, so the chart's other panels can be
    // showing a stale picture of what the patient's next session will do.
    void queryClient.invalidateQueries({ queryKey: ['prescription', patientId] });
  };

  const review = useMutation({
    mutationFn: (eventId: string) =>
      api.post(`/patients/${patientId}/safety-events/${eventId}/review`),
    onSuccess: invalidate,
  });

  const restore = useMutation({
    mutationFn: (reductionId: string) =>
      api.post(`/patients/${patientId}/dose-reductions/${reductionId}/restore`),
    onSuccess: invalidate,
  });

  if (loaded.isLoading) return null;
  if (loaded.isError || !loaded.data) {
    return (
      <Panel title="Safety events" summary="Could not load these">
        <p className={styles.empty}>Could not load these. Nothing has been changed.</p>
      </Panel>
    );
  }

  const { events, progressionLocked } = loaded.data;
  const unreviewed = events.filter((event) => event.reviewedAt === null).length;

  return (
    <Panel
      title="Safety events"
      summary={shutSummary(events.length, unreviewed, progressionLocked)}
      /*
        Opens by itself when something has not been looked at. This is the one
        panel on the page whose whole purpose is to be noticed, and a chevron
        that hid an unreviewed event would defeat it.
      */
      defaultOpen={unreviewed > 0}
      aside={
        progressionLocked ? <span className={styles.lockFlag}>Running at reduced dose</span> : null
      }
    >
      {progressionLocked && (
        <p className={styles.lockNote}>
          This patient&rsquo;s sessions are running at two-thirds of the
          prescribed duration, and the program will not advance. That clears by
          itself when they complete a full session at the reduced dose — or now,
          if you restore it below.
        </p>
      )}

      {events.length === 0 ? (
        <p className={styles.empty}>
          Nothing has ended early. Sessions that stop — whether the patient stops
          them or the system does — appear here with what happened.
        </p>
      ) : (
        <ol className={styles.list}>
          {events.map((event) => (
            <li
              key={event.id}
              className={event.reviewedAt ? styles.event : styles.eventUnreviewed}
            >
              <div className={styles.eventHead}>
                <span className={styles.cause}>{event.cause}</span>
                <span className={styles.when}>{formatWhen(event.triggeredAt)}</span>
              </div>

              <p className={styles.context}>
                {event.sessionLabel ? `Session ${event.sessionLabel}` : 'Before the session began'}
                {event.exerciseId ? ` · ${event.exerciseId}` : ''}
              </p>

              {event.reduction && (
                <p className={styles.reduction}>
                  {event.reduction.restoredAt ? (
                    <>Dose restored {formatWhen(event.reduction.restoredAt)}.</>
                  ) : (
                    <>
                      Next sessions reduced to two-thirds duration.{' '}
                      <button
                        type="button"
                        className={styles.restore}
                        disabled={restore.isPending}
                        onClick={() => restore.mutate(event.reduction!.id)}
                      >
                        Restore full dose
                      </button>
                    </>
                  )}
                </p>
              )}

              <Trace data={event.triggerData} />

              {!event.reviewedAt && (
                <button
                  type="button"
                  className={styles.reviewed}
                  disabled={review.isPending}
                  onClick={() => review.mutate(event.id)}
                >
                  Mark as seen
                </button>
              )}
            </li>
          ))}
        </ol>
      )}
    </Panel>
  );
}

/**
 * The state in one line, for when the panel is shut.
 *
 * Says what is outstanding rather than what exists. A count of everything that
 * ever happened is history; a count of what has not been looked at is a
 * prompt, and this panel is a prompt.
 */
function shutSummary(total: number, unreviewed: number, locked: boolean): string {
  if (total === 0) return 'Nothing has ended early';
  const events = total === 1 ? '1 event' : `${total} events`;
  const seen = unreviewed > 0 ? `${unreviewed} not yet seen` : 'all seen';
  return locked ? `${events} · ${seen} · reduced dose in force` : `${events} · ${seen}`;
}

/**
 * What the system saw, in sentences.
 *
 * Shown open rather than behind a disclosure: it is two or three lines, and
 * hiding the reason behind a click on a screen the doctor opens BECAUSE
 * something happened is friction in the wrong direction.
 */
function Trace({ data }: { data: unknown }) {
  const lines = describeTrigger(data);
  if (lines.length === 0) return null;
  return (
    <dl className={styles.trace}>
      {lines.map((line) => (
        <div key={line.label} className={styles.traceRow}>
          <dt className={styles.traceLabel}>{line.label}</dt>
          <dd className={styles.traceValue}>{line.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** Date and time, because "when did this happen" is the first question. */
function formatWhen(iso: string): string {
  const at = new Date(iso);
  return at.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });
}
